import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTourGuide } from "./TourGuideProvider";
import { useTourAnchor } from "./hooks/useTourAnchor";
import { TourSpotlight } from "./TourSpotlight";
import { Button } from "@/components/ui/button";
import { X, ChevronLeft, ChevronRight, SkipForward, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import type { TourPlacement } from "./types";

const TOOLTIP_WIDTH = 420;
const TOOLTIP_GAP = 14;
const VIEWPORT_PAD = 12;
const MOBILE_BP = 768;

interface RectLike { top: number; left: number; width: number; height: number }

/** Clamp a candidate position inside the viewport with the measured tooltip size. */
function clampToViewport(top: number, left: number, w: number, h: number) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  return {
    top: Math.max(VIEWPORT_PAD, Math.min(top, vh - h - VIEWPORT_PAD)),
    left: Math.max(VIEWPORT_PAD, Math.min(left, vw - w - VIEWPORT_PAD)),
  };
}

function computeTooltipPosition(
  rect: RectLike,
  preferred: TourPlacement = "auto",
  measuredHeight: number,
): { top: number; left: number; placement: Exclude<TourPlacement, "auto"> } {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const h = measuredHeight;

  const candidates: Record<Exclude<TourPlacement, "auto">, { top: number; left: number }> = {
    bottom: {
      top: rect.top + rect.height + TOOLTIP_GAP,
      left: rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2,
    },
    top: {
      top: rect.top - TOOLTIP_GAP - h,
      left: rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2,
    },
    right: {
      top: rect.top + rect.height / 2 - h / 2,
      left: rect.left + rect.width + TOOLTIP_GAP,
    },
    left: {
      top: rect.top + rect.height / 2 - h / 2,
      left: rect.left - TOOLTIP_GAP - TOOLTIP_WIDTH,
    },
  };

  const fits = (p: Exclude<TourPlacement, "auto">) => {
    const c = candidates[p];
    return (
      c.top >= VIEWPORT_PAD &&
      c.left >= VIEWPORT_PAD &&
      c.top + h <= vh - VIEWPORT_PAD &&
      c.left + TOOLTIP_WIDTH <= vw - VIEWPORT_PAD
    );
  };

  const order: Exclude<TourPlacement, "auto">[] =
    preferred === "auto"
      ? ["bottom", "top", "right", "left"]
      : [preferred, ...(["bottom", "top", "right", "left"].filter((p) => p !== preferred) as Exclude<TourPlacement, "auto">[])];

  for (const p of order) {
    if (fits(p)) {
      const c = candidates[p];
      const clamped = clampToViewport(c.top, c.left, TOOLTIP_WIDTH, h);
      return { placement: p, ...clamped };
    }
  }

  // Nothing fits perfectly — clamp the best candidate
  const c = candidates.bottom;
  const clamped = clampToViewport(c.top, c.left, TOOLTIP_WIDTH, h);
  return { placement: "bottom", ...clamped };
}

/**
 * The tour tooltip — anchored, animated, mobile-aware, keyboard-accessible.
 * - Desktop "anchored": floats next to anchored target with auto-flip placement
 * - Desktop "fixed":    stays in a fixed corner; only the spotlight moves
 * - Mobile (<768px):    bottom sheet, full-width
 */
export const TourTooltip = () => {
  const { language, isRTL } = useLanguage();
  const { isOpen, currentStep, close, next, back, skip, selectOption, progressRatio, preferences } = useTourGuide();
  const fa = language === "fa";

  const containerRef = useRef<HTMLDivElement>(null);
  const isMobile = typeof window !== "undefined" && window.innerWidth < MOBILE_BP;
  const tooltipMode = preferences?.tooltip_mode ?? "anchored";
  const useAnchored = tooltipMode === "anchored" && !isMobile;

  const { rect, element } = useTourAnchor({
    targetId: currentStep?.targetId,
    selector: currentStep?.highlightSelector,
    enabled: isOpen, // resolve target for spotlight even in fixed mode
  });

  // Measure the rendered tooltip so we can clamp accurately
  const [measuredH, setMeasuredH] = useState(260);
  useLayoutEffect(() => {
    if (!isOpen || !containerRef.current) return;
    const el = containerRef.current;
    const measure = () => setMeasuredH(el.getBoundingClientRect().height || 260);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isOpen, currentStep?.id]);

  const positioned = useMemo(() => {
    if (!useAnchored || !rect) return null;
    return computeTooltipPosition(rect, currentStep?.placement, measuredH);
  }, [useAnchored, rect, currentStep?.placement, measuredH]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        close();
      } else if (e.key === "ArrowRight") {
        if (!isRTL) next();
        else back();
      } else if (e.key === "ArrowLeft") {
        if (!isRTL) back();
        else next();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, close, next, back, isRTL]);

  // Outside-click → close reliably on any click outside the tooltip itself
  useEffect(() => {
    if (!isOpen) return;
    const onPointerDown = (e: Event) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (containerRef.current?.contains(target)) return;
      close();
    };
    // Defer one tick so the opening click doesn't immediately close it
    const id = window.setTimeout(() => {
      document.addEventListener("pointerdown", onPointerDown, true);
      document.addEventListener("click", onPointerDown, true);
    }, 50);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("click", onPointerDown, true);
    };
  }, [isOpen, close]);

  // Focus first interactive element on open
  useEffect(() => {
    if (!isOpen) return;
    const id = setTimeout(() => {
      const focusable = containerRef.current?.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      focusable?.focus();
    }, 100);
    return () => clearTimeout(id);
  }, [isOpen, currentStep?.id]);

  if (!currentStep) return null;

  const title = fa ? currentStep.titleFa : currentStep.titleEn;
  const body = fa ? currentStep.bodyFa : currentStep.bodyEn;
  const canGoBack = !!currentStep.previousStepId;
  const canGoNext = !!currentStep.nextStepId;
  const progressPct = Math.round(progressRatio * 100);

  // Position style
  const desktopAnchored: React.CSSProperties = positioned
    ? { top: positioned.top, left: positioned.left, width: TOOLTIP_WIDTH }
    : {};

  // Fixed-mode (or anchored with no target) → top-right/left corner
  const desktopFixedClass = (!useAnchored || !positioned) && !isMobile
    ? cn(
        "top-[120px] lg:top-[124px]",
        isRTL ? "left-4 lg:left-8" : "right-4 lg:right-8",
        "w-[min(550px,calc(100vw-2rem))]",
      )
    : "";

  const mobileClass = isMobile ? "left-2 right-2 bottom-2 w-auto" : "";

  return (
    <>
      <AnimatePresence>
        {isOpen && rect && !isMobile && (
          <motion.div
            key={`spot-${currentStep.id}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            <TourSpotlight rect={rect} element={element} />
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence mode="wait">
        {isOpen && (
          <motion.div
            key={`tip-${currentStep.id}-${tooltipMode}`}
            ref={containerRef}
            className={cn("fixed z-40", desktopFixedClass, mobileClass)}
            style={isMobile ? {} : desktopAnchored}
            role="dialog"
            aria-modal="false"
            aria-labelledby="tour-tooltip-title"
            aria-describedby="tour-tooltip-body"
            initial={isMobile ? { y: 100, opacity: 0 } : useAnchored ? { y: -8, opacity: 0, scale: 0.97 } : { opacity: 0 }}
            animate={isMobile ? { y: 0, opacity: 1 } : useAnchored ? { y: 0, opacity: 1, scale: 1 } : { opacity: 1 }}
            exit={isMobile ? { y: 100, opacity: 0 } : useAnchored ? { y: -8, opacity: 0, scale: 0.97 } : { opacity: 0 }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="rounded-xl border border-primary/30 bg-card shadow-elevated overflow-hidden">
              <div className="h-1 w-full bg-muted">
                <div
                  className="h-full bg-gradient-to-r from-primary to-primary/70 transition-all duration-500"
                  style={{ width: `${progressPct}%` }}
                />
              </div>

              <div className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <Sparkles className="h-4 w-4 text-primary shrink-0" />
                    <h3 id="tour-tooltip-title" className="font-semibold text-sm text-foreground truncate">
                      {title}
                    </h3>
                  </div>
                  <button
                    onClick={close}
                    className="p-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors shrink-0"
                    aria-label={fa ? "بستن (Esc)" : "Close (Esc)"}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                <p id="tour-tooltip-body" className="text-sm text-muted-foreground leading-relaxed">
                  {body}
                </p>

                {currentStep.options && currentStep.options.length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-1">
                    {currentStep.options.map((opt) => (
                      <Button
                        key={opt.id}
                        size="sm"
                        variant="secondary"
                        className="h-8 text-xs"
                        onClick={() => selectOption(opt)}
                      >
                        {fa ? opt.labelFa : opt.labelEn}
                      </Button>
                    ))}
                  </div>
                )}

                <div className="flex items-center justify-between gap-2 pt-2 border-t border-border">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={back}
                    disabled={!canGoBack}
                    aria-label={fa ? "قبلی (فلش)" : "Back (Arrow)"}
                  >
                    <ChevronLeft className={cn("h-3.5 w-3.5", isRTL && "rotate-180")} />
                    {fa ? "قبلی" : "Back"}
                  </Button>

                  <span className="text-[10px] text-muted-foreground font-mono">{progressPct}%</span>

                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs text-muted-foreground"
                      onClick={skip}
                    >
                      <SkipForward className="h-3.5 w-3.5" />
                      {fa ? "رد کن" : "Skip"}
                    </Button>
                    <Button
                      variant="default"
                      size="sm"
                      className="h-7 px-3 text-xs"
                      onClick={next}
                      disabled={!canGoNext}
                      aria-label={fa ? "بعدی (فلش)" : "Next (Arrow)"}
                    >
                      {fa ? "بعدی" : "Next"}
                      <ChevronRight className={cn("h-3.5 w-3.5", isRTL && "rotate-180")} />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
